import { useAuth } from '@/domain/context/auth';
import {
  CollectionBase,
  CriticalStandard,
  Link,
  Newsletter,
  Podcast,
  Report,
  Video,
} from '@/domain/types/collections';
import { fetchAudiencesEnabled } from '@/domain/use-cases/audiences/audience-settings';
import { audienceIdsForPublication } from '@/domain/use-cases/audiences/membership-rules';
import uploadFile from '@/domain/use-cases/upload-file';
import useFormDraft, {
  draftImageToFile,
  fileToDraftImage,
  FormDraftImage,
} from '@/presentation/hooks/use-form-draft';
import useIsMobile from '@/presentation/hooks/use-is-mobile';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FiSave, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { z } from 'zod';
import AudienceSelect from '../audience-select';
import AudioUpload from '../audio-upload';
import Button from '../button';
import ImageUpload from '../image-upload';
import { Input } from '../inputs/input';
import { InputForm } from '../inputs/input-form';
import { SelectForm } from '../inputs/select-form';
import { TextAreaForm } from '../inputs/text-area-form';
import PdfUpload from '../pdf-upload';
import VideoUpload from '../video-upload';
import ReviewSummary from './review-summary';
import StepProgress from './step-progress';

const documentSchema: z.ZodType<CollectionBase> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
});

const criticalStandardSchema: z.ZodType<CriticalStandard> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  category: z.enum(['critical', 'life-saving', 'tar', 'other'], {
    errorMap: () => ({ message: 'La categoría es requerida' }),
  }),
});

const newsletterSchema: z.ZodType<Newsletter> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  category: z.enum(['publicacion', 'alerta'], {
    errorMap: () => ({ message: 'La categoría es requerida' }),
  }),
});

const reportSchema: z.ZodType<Report> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  category: z.enum(
    ['blasting', 'meeting', 'brigades', 'safety', 'incidents', 'maintenance'],
    {
      errorMap: () => ({ message: 'La categoría es requerida' }),
    },
  ),
});

const linkSchema: z.ZodType<Link> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('El enlace en español debe ser una URL válida')
    .min(1, 'El enlace en español es requerido'),
  linkEnglish: z
    .string()
    .url('El enlace en inglés debe ser una URL válida')
    .min(1, 'El enlace en inglés es requerido'),
});

const podcastSchema: z.ZodType<Podcast> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  category: z.enum(['podcast', 'audio'], {
    errorMap: () => ({ message: 'La categoría es requerida' }),
  }),
});

const videoSchema: z.ZodType<Video> = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  descriptionEN: z.string().min(1, 'La descripción en inglés es requerida'),
  linkSpanish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  linkEnglish: z
    .string()
    .url('Debe ser una URL válida')
    .optional()
    .or(z.literal('')),
  category: z.enum(['video', 'podcast'], {
    errorMap: () => ({ message: 'La categoría es requerida' }),
  }),
});

const tagSchema = z.object({
  tag: z.string().min(1, 'El tag es requerido'),
});

const STEP_LABELS = [
  'Contenido en español',
  'Contenido en inglés',
  'Imagen y tags',
  'Revisión y publicar',
] as const;

interface FormAddDocumentProps {
  onSave: (
    data:
      | CollectionBase
      | Newsletter
      | Report
      | Link
      | CriticalStandard
      | Podcast
      | Video,
  ) => void | Promise<void>;
  document?:
    | CollectionBase
    | Newsletter
    | Report
    | Link
    | CriticalStandard
    | Podcast
    | Video;
  hasAudio?: boolean;
  hasImage?: boolean;
  hasPdf?: boolean;
  hasVideo?: boolean;
  contentType?:
    | 'newsletter'
    | 'report'
    | 'link'
    | 'critical-standard'
    | 'podcast'
    | 'video'
    | 'default';
  showAudienceSelector?: boolean;
}

const FormAddDocument = ({
  onSave,
  document,
  hasAudio = false,
  hasImage = true,
  hasPdf = true,
  hasVideo = false,
  contentType = 'default',
  showAudienceSelector = false,
}: FormAddDocumentProps) => {
  // Para links, deshabilitamos archivos innecesarios
  const actualHasAudio = contentType === 'link' ? false : hasAudio;
  const actualHasPdf = contentType === 'link' ? false : hasPdf;
  const actualHasVideo = contentType === 'link' ? false : hasVideo;
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [imageEN, setImageEN] = useState<File | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [pdfEN, setPdfEN] = useState<File | null>(null);
  const [audio, setAudio] = useState<File | null>(null);
  const [audioEN, setAudioEN] = useState<File | null>(null);
  const [video, setVideo] = useState<File | null>(null);
  const [videoEN, setVideoEN] = useState<File | null>(null);

  const [tagInputValue, setTagInputValue] = useState('');
  const [tagInputError, setTagInputError] = useState<string | null>(null);
  const [tagENInputValue, setTagENInputValue] = useState('');
  const [tagENInputError, setTagENInputError] = useState<string | null>(null);

  const [tags, setTags] = useState<{ tag: string }[]>(
    document?.tags?.map((tag: string) => ({ tag })) || [],
  );
  const [tagsEN, setTagsEN] = useState<{ tag: string }[]>(
    document?.tagsEN?.map((tag: string) => ({ tag })) || [],
  );

  // Por defecto público: preserva el comportamiento actual y hace que el error
  // posible sea visible (se publicó algo que debía restringirse) en vez de
  // silencioso (se publicó algo que nadie ve).
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>(
    document?.audienceIds && !document.audienceIds.includes('ALL')
      ? document.audienceIds
      : [],
  );

  // Mientras el interruptor esté apagado no se ofrece restringir: habría usuarios
  // con la app vieja, sin filtro, que verían el contenido restringido igual.
  const [audiencesEnabled, setAudiencesEnabled] = useState(false);

  useEffect(() => {
    if (!showAudienceSelector) return;
    fetchAudiencesEnabled().then(setAudiencesEnabled);
  }, [showAudienceSelector]);

  const isNewsletter = contentType === 'newsletter';
  const isReport = contentType === 'report';
  const isLink = contentType === 'link';
  const isCriticalStandard = contentType === 'critical-standard';
  const isPodcast = contentType === 'podcast';
  const isVideo = contentType === 'video';

  let schema;
  if (isNewsletter) {
    schema = newsletterSchema;
  } else if (isReport) {
    schema = reportSchema;
  } else if (isLink) {
    schema = linkSchema;
  } else if (isCriticalStandard) {
    schema = criticalStandardSchema;
  } else if (isPodcast) {
    schema = podcastSchema;
  } else if (isVideo) {
    schema = videoSchema;
  } else {
    schema = documentSchema;
  }

  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isCreateMode = !document;
  const hasCategory =
    isNewsletter || isReport || isCriticalStandard || isPodcast || isVideo;

  const stepFieldNames: string[][] = [
    [...(hasCategory ? ['category'] : []), 'title', 'description', 'linkSpanish'],
    ['titleEN', 'descriptionEN', 'linkEnglish'],
    [],
    [],
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = STEP_LABELS.length;
  const isStepVisible = (step: number) => !isMobile || currentStep === step;

  // Guards against a fast double-tap landing on "Guardar" right after it
  // appears in the exact screen position "Siguiente" occupied a moment
  // earlier: React reuses the same DOM button element when "Siguiente"
  // (type="button") is replaced by "Guardar" (type="submit") at that same
  // position on the last step, so a second tap arriving within this window —
  // common on touchscreens — would otherwise submit the form without the
  // user having consciously clicked "Guardar". Only this specific
  // step-to-step transition changes both the button's label and its type,
  // so only it needs the cooldown; moving between earlier "Siguiente" steps
  // is a harmless, idempotent no-op if double-tapped.
  //
  // The disable must take effect on the SAME render that first shows
  // "Guardar" — a useEffect-based disable would run one render too late,
  // leaving the very first paint of "Guardar" fully enabled. So this adjusts
  // state during render (the documented React pattern for "reset/derive
  // state when a value changes"), not in an effect: React re-renders
  // immediately with the updated state before anything is committed to the
  // screen, so "Guardar" is never painted enabled before it's disabled.
  const isOnLastStep = currentStep === totalSteps - 1;
  const GUARDAR_APPEAR_COOLDOWN_MS = 400;
  const [isGuardarCoolingDown, setIsGuardarCoolingDown] = useState(false);
  const previousStepRef = useRef(currentStep);

  if (previousStepRef.current !== currentStep) {
    previousStepRef.current = currentStep;
    if (isOnLastStep && !isGuardarCoolingDown) {
      setIsGuardarCoolingDown(true);
    }
  }

  useEffect(() => {
    if (!isGuardarCoolingDown) return;
    const timeoutId = setTimeout(
      () => setIsGuardarCoolingDown(false),
      GUARDAR_APPEAR_COOLDOWN_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [isGuardarCoolingDown]);

  const methods = useForm<
    CollectionBase | Newsletter | Report | CriticalStandard | Podcast
  >({
    resolver: zodResolver(schema),
    defaultValues: document,
  });

  const draftKey = `draft:form-add-document:${contentType}:${user?.id ?? 'anonymous'}`;
  const { saveDraft, loadDraft, clearDraft } = useFormDraft(draftKey);
  // Computed synchronously via the lazy useState initializer (not in a
  // useEffect) so it's already correct on this component's very FIRST
  // render. Loading it in an effect instead created a race: the autosave
  // effect below runs in that same initial effects flush and would see
  // `pendingDraft` still at its stale pre-update value (null) — reading an
  // empty form with no pending draft — and call clearDraft(), wiping the
  // just-loaded draft from storage before the user ever saw the restore
  // banner. A lazy initializer has no such lag; it IS the first render.
  const [pendingDraft, setPendingDraft] = useState<ReturnType<
    typeof loadDraft
  >>(() => (isCreateMode ? loadDraft() : null));
  const [draftImageCache, setDraftImageCache] = useState<FormDraftImage | null>(
    null,
  );
  const [draftImageENCache, setDraftImageENCache] =
    useState<FormDraftImage | null>(null);

  const applyPendingDraft = () => {
    if (!pendingDraft) return;
    methods.reset(pendingDraft.fields as never);
    setTags(pendingDraft.tags);
    setTagsEN(pendingDraft.tagsEN);
    if (pendingDraft.image) {
      setImage(draftImageToFile(pendingDraft.image));
      setDraftImageCache(pendingDraft.image);
    }
    if (pendingDraft.imageEN) {
      setImageEN(draftImageToFile(pendingDraft.imageEN));
      setDraftImageENCache(pendingDraft.imageEN);
    }
    setPendingDraft(null);
  };

  const discardPendingDraft = () => {
    clearDraft();
    setPendingDraft(null);
  };

  const watchedFields = methods.watch();
  const watchedFieldsKey = JSON.stringify(watchedFields);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const encoded = image ? await fileToDraftImage(image) : null;
      if (!cancelled) setDraftImageCache(encoded);
    })();

    return () => {
      cancelled = true;
    };
  }, [image]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const encoded = imageEN ? await fileToDraftImage(imageEN) : null;
      if (!cancelled) setDraftImageENCache(encoded);
    })();

    return () => {
      cancelled = true;
    };
  }, [imageEN]);

  const hasDraftContent =
    Object.values(watchedFields).some(
      (value) => typeof value === 'string' && value.trim().length > 0,
    ) ||
    tags.length > 0 ||
    tagsEN.length > 0 ||
    Boolean(draftImageCache) ||
    Boolean(draftImageENCache);

  useEffect(() => {
    // The restore banner isn't modal — a user can ignore it and start
    // typing a brand new post instead of deciding "Continuar"/"Empezar de
    // nuevo" first. Without this, the autosave effect below stays blocked
    // by `pendingDraft` for as long as the banner is unanswered, so the
    // new content the user is actively writing gets zero autosave
    // protection while the OLD draft keeps being offered for restore.
    // Any real edit while the banner is up is an implicit "empezar de
    // nuevo": stop offering the old draft so the new content can be saved.
    if (pendingDraft && hasDraftContent) setPendingDraft(null);
  }, [pendingDraft, hasDraftContent]);

  useEffect(() => {
    if (!isCreateMode || pendingDraft) return;

    // Don't persist (or keep) a draft for a form nobody has touched yet —
    // otherwise just opening "Agregar" and navigating away leaves a
    // pointless "tienes un borrador" banner with nothing in it. If the
    // form previously had content and was fully cleared back to empty,
    // this also cleans up that now-stale draft instead of leaving it
    // behind.
    if (!hasDraftContent) {
      clearDraft();
      return;
    }

    saveDraft({
      fields: watchedFields,
      tags,
      tagsEN,
      image: draftImageCache,
      imageEN: draftImageENCache,
    });
    // watchedFieldsKey is a stable stringified snapshot used instead of
    // watchedFields itself, since methods.watch() returns a new object
    // reference on every render and would otherwise re-run this effect on
    // every render instead of only on actual value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedFieldsKey,
    tags,
    tagsEN,
    draftImageCache,
    draftImageENCache,
    isCreateMode,
    pendingDraft,
    hasDraftContent,
  ]);

  const goToNextStep = async () => {
    const isStepValid = await methods.trigger(
      stepFieldNames[currentStep] as never,
    );
    if (!isStepValid) return;
    setCurrentStep((step) => Math.min(step + 1, totalSteps - 1));
  };

  const goToPreviousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const onSubmit = async (
    data: CollectionBase | Newsletter | Report | CriticalStandard | Podcast,
  ) => {
    setIsLoading(true);
    try {
      const newDocument:
        | CollectionBase
        | Newsletter
        | Report
        | CriticalStandard
        | Podcast = {
        ...data,
        tags: tags.map((tag) => tag.tag),
        tagsEN: tagsEN.map((tag) => tag.tag),
        ...(showAudienceSelector
          ? { audienceIds: audienceIdsForPublication(selectedAudiences) }
          : {}),
      };
      if (image) {
        newDocument.image = await uploadFile({ file: image, path: 'images' });
      }
      if (imageEN) {
        newDocument.imageEN = await uploadFile({
          file: imageEN,
          path: 'images',
        });
      }
      if (pdf) {
        newDocument.documentUrl = await uploadFile({ file: pdf, path: 'pdfs' });
      }
      if (pdfEN) {
        newDocument.documentUrlEN = await uploadFile({
          file: pdfEN,
          path: 'pdfs',
        });
      }
      if (audio) {
        newDocument.uri = await uploadFile({ file: audio, path: 'audios' });
      }
      if (audioEN) {
        newDocument.uriEN = await uploadFile({ file: audioEN, path: 'audios' });
      }
      if (video) {
        newDocument.uri = await uploadFile({ file: video, path: 'videos' });
      }
      if (videoEN) {
        newDocument.uriEN = await uploadFile({ file: videoEN, path: 'videos' });
      }

      try {
        await onSave(newDocument);
        if (isCreateMode) clearDraft();
      } catch (saveError) {
        // onSave (the page-level handler) is responsible for its own
        // user-facing error message (e.g. a toast) — we only need to know
        // the write failed so we don't delete the draft that's the user's
        // only remaining copy of their work. Re-showing a second, generic
        // error here would be confusing on top of that specific one.
        console.error('Error saving document:', saveError);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir el documento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.includes(' ')) {
      const tagValue = value.trim();
      if (tagValue.length > 0) {
        try {
          const newTag = { tag: tagValue };
          tagSchema.parse(newTag);
          setTags([...tags, newTag]);
          setTagInputError(null);
        } catch (error) {
          if (error instanceof z.ZodError) {
            setTagInputError(error.errors[0].message);
          }
        }
      }
      setTagInputValue('');
      return;
    }

    setTagInputValue(value);
    setTagInputError(null);
  };

  const handleTagENChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.includes(' ')) {
      const tagValue = value.trim();
      if (tagValue.length > 0) {
        try {
          const newTag = { tag: tagValue };
          tagSchema.parse(newTag);
          setTagsEN([...tagsEN, newTag]);
          setTagENInputError(null);
        } catch (error) {
          if (error instanceof z.ZodError) {
            setTagENInputError(error.errors[0].message);
          }
        }
      }
      setTagENInputValue('');
      return;
    }

    setTagENInputValue(value);
    setTagENInputError(null);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      try {
        const tagValue = tagInputValue.trim();

        if (tagValue.length === 0) {
          setTagInputError('El tag es requerido');
          return;
        }

        const newTag = { tag: tagValue };
        tagSchema.parse(newTag);

        setTags([...tags, newTag]);
        setTagInputError(null);
        setTagInputValue('');
      } catch (error) {
        if (error instanceof z.ZodError) {
          setTagInputError(error.errors[0].message);
        }
      }
    }
  };

  const handleTagENKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      try {
        const tagValue = tagENInputValue.trim();

        if (tagValue.length === 0) {
          setTagENInputError('El tag es requerido');
          return;
        }

        const newTag = { tag: tagValue };
        tagSchema.parse(newTag);

        setTagsEN([...tagsEN, newTag]);
        setTagENInputError(null);
        setTagENInputValue('');
      } catch (error) {
        if (error instanceof z.ZodError) {
          setTagENInputError(error.errors[0].message);
        }
      }
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t.tag !== tag));
  };

  const handleRemoveTagEN = (tag: string) => {
    setTagsEN(tagsEN.filter((t) => t.tag !== tag));
  };

  const handleTagPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const inputTags = pastedText
      .split(' ')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    if (inputTags.length === 0) return;

    try {
      const newTags = inputTags.map((tagValue) => {
        const newTag = { tag: tagValue };
        tagSchema.parse(newTag);
        return newTag;
      });

      setTags([...tags, ...newTags]);
      setTagInputError(null);
      setTagInputValue('');
    } catch (error) {
      if (error instanceof z.ZodError) {
        setTagInputError(error.errors[0].message);
      }
    }
  };

  const handleTagENPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const inputTags = pastedText
      .split(' ')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    if (inputTags.length === 0) return;

    try {
      const newTags = inputTags.map((tagValue) => {
        const newTag = { tag: tagValue };
        tagSchema.parse(newTag);
        return newTag;
      });

      setTagsEN([...tagsEN, ...newTags]);
      setTagENInputError(null);
      setTagENInputValue('');
    } catch (error) {
      if (error instanceof z.ZodError) {
        setTagENInputError(error.errors[0].message);
      }
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {isCreateMode && pendingDraft && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span className="text-sm text-yellow-900">
            Tienes un borrador sin publicar de{' '}
            {new Date(pendingDraft.savedAt).toLocaleString()}.
          </span>
          <div className="flex gap-2">
            <Button
              label="Continuar borrador"
              type="button"
              onClick={applyPendingDraft}
              className="w-[180px]"
            />
            <Button
              label="Empezar de nuevo"
              type="button"
              variant="secondary"
              onClick={discardPendingDraft}
              className="w-[180px]"
            />
          </div>
        </div>
      )}
      {isMobile && (
        <StepProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          label={STEP_LABELS[currentStep]}
        />
      )}
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div
          className={`flex flex-col gap-4 flex-1 bg-white rounded-lg shadow-md p-4 md:p-12 ${
            isStepVisible(2) ? '' : 'hidden'
          }`}
        >
          {hasImage && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">Imagen en español</span>
              <ImageUpload
                onFileChange={setImage}
                image={document?.image ?? draftImageCache?.dataUrl}
                id="image-upload"
              />
              <span className="text-sm text-gray-500">Imagen en inglés</span>
              <ImageUpload
                onFileChange={setImageEN}
                image={document?.imageEN ?? draftImageENCache?.dataUrl}
                id="image-upload-en"
              />
            </div>
          )}
          {actualHasPdf && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">Documento en español</span>
              <PdfUpload
                onFileChange={setPdf}
                pdf={document?.documentUrl}
                id="pdf-upload"
              />
              <span className="text-sm text-gray-500">Documento en inglés</span>
              <PdfUpload
                onFileChange={setPdfEN}
                pdf={document?.documentUrlEN}
                id="pdf-upload-en"
              />
            </div>
          )}
          {actualHasAudio && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">Audio en español</span>
              <AudioUpload
                onFileChange={setAudio}
                audio={document?.uri}
                id="audio-upload"
              />
              <span className="text-sm text-gray-500">Audio en inglés</span>
              <AudioUpload
                onFileChange={setAudioEN}
                audio={document?.uriEN}
                id="audio-upload-en"
              />
            </div>
          )}
          {actualHasVideo && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">Video en español</span>
              <VideoUpload
                onFileChange={setVideo}
                video={document?.uri}
                id="video-upload"
              />
              <span className="text-sm text-gray-500">Video en inglés</span>
              <VideoUpload
                onFileChange={setVideoEN}
                video={document?.uriEN}
                id="video-upload-en"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 flex-1 bg-white rounded-lg shadow-md p-4 md:p-12">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-4">
                <div className={isStepVisible(0) ? '' : 'hidden'}>
                  {isNewsletter && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Categoría del contenido
                      </label>
                      <SelectForm
                        name="category"
                        placeholder="Selecciona una categoría"
                        options={[
                          { value: 'publicacion', label: 'Publicación' },
                          { value: 'alerta', label: 'Alerta' },
                        ]}
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        Selecciona si es una publicación regular o una alerta
                        importante
                      </p>
                    </div>
                  )}
                  {isReport && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <label className="block text-sm font-medium text-green-900 mb-2">
                        Categoría del reporte
                      </label>
                      <SelectForm
                        name="category"
                        placeholder="Selecciona una categoría"
                        options={[
                          {
                            value: 'blasting',
                            label: 'Blasting Schedule / Cronograma de Voladura',
                          },
                          {
                            value: 'meeting',
                            label:
                              'Mine Daily Meeting Safety Report / Reporte de Seguridad de Reunión Diaria de Mina',
                          },
                          {
                            value: 'brigades',
                            label:
                              'Brigades Training Report / Reporte de Entrenamiento de Brigadas',
                          },
                          {
                            value: 'safety',
                            label:
                              'Safety Training Reports / Reportes de Entrenamiento de Seguridad',
                          },
                          {
                            value: 'incidents',
                            label:
                              'Shift Incidents Command System / Sistema de Comando de Incidentes de Turno',
                          },
                          {
                            value: 'maintenance',
                            label: 'Maintenance Report / Reporte de Mantenimiento',
                          },
                        ]}
                      />
                      <p className="text-xs text-green-600 mt-1">
                        Selecciona el tipo de reporte que estás creando
                      </p>
                    </div>
                  )}
                  {isCriticalStandard && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <label className="block text-sm font-medium text-yellow-900 mb-2">
                        Categoría del estándar
                      </label>
                      <SelectForm
                        name="category"
                        placeholder="Selecciona una categoría"
                        options={[
                          { value: 'critical', label: 'Estándares Críticos' },
                          {
                            value: 'life-saving',
                            label: 'Reglas para Salvar Vidas',
                          },
                          { value: 'tar', label: 'TAR' },
                          { value: 'other', label: 'Otros Estándares' },
                        ]}
                      />
                      <p className="text-xs text-yellow-600 mt-1">
                        Selecciona la categoría correspondiente para el estándar
                      </p>
                    </div>
                  )}
                  {isPodcast && (
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <label className="block text-sm font-medium text-purple-900 mb-2">
                        Tipo de contenido de audio
                      </label>
                      <SelectForm
                        name="category"
                        placeholder="Selecciona un tipo"
                        options={[
                          { value: 'podcast', label: 'Podcast' },
                          { value: 'audio', label: 'Audio / Publicación de audio' },
                        ]}
                      />
                      <p className="text-xs text-purple-600 mt-1">
                        Selecciona si es un podcast o una publicación de audio
                        regular
                      </p>
                    </div>
                  )}
                  {isVideo && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Tipo de contenido de video
                      </label>
                      <SelectForm
                        name="category"
                        placeholder="Selecciona un tipo"
                        options={[
                          { value: 'video', label: 'Video / Publicación de video' },
                          { value: 'podcast', label: 'Podcast' },
                        ]}
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        Selecciona si es un video o un podcast
                      </p>
                    </div>
                  )}
                  <InputForm name="title" placeholder="Título" />
                  <TextAreaForm name="description" placeholder="Descripción" />
                  <InputForm
                    name="linkSpanish"
                    placeholder="Link en español (opcional)"
                    type="url"
                  />
                </div>

                <div className={isStepVisible(1) ? '' : 'hidden'}>
                  <InputForm name="titleEN" placeholder="Título en inglés" />
                  <TextAreaForm
                    name="descriptionEN"
                    placeholder="Descripción en inglés"
                  />
                  <InputForm
                    name="linkEnglish"
                    placeholder="Link en inglés (opcional)"
                    type="url"
                  />
                </div>

                <div className={isStepVisible(2) ? '' : 'hidden'}>
                  <div className="flex flex-col gap-2">
                    <Input
                      type="text"
                      value={tagInputValue}
                      onChange={handleTagChange}
                      onKeyDown={handleTagKeyDown}
                      onPaste={handleTagPaste}
                      placeholder="Agregar tag"
                    />
                    <span className="text-sm text-gray-500">
                      Presiona Enter para agregar un tag
                    </span>
                    {tagInputError && (
                      <p className="text-red-500">{tagInputError}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <div
                          key={tag.tag}
                          className="flex items-center justify-between gap-2 bg-gray-500 p-2 rounded-md"
                        >
                          <span className="text-sm text-white">{tag.tag}</span>
                          <button
                            className="text-sm text-white"
                            onClick={() => handleRemoveTag(tag.tag)}
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Input
                      type="text"
                      value={tagENInputValue}
                      onChange={handleTagENChange}
                      onKeyDown={handleTagENKeyDown}
                      onPaste={handleTagENPaste}
                      placeholder="Agregar tag en inglés"
                    />
                    <span className="text-sm text-gray-500">
                      Presiona Enter para agregar un tag en inglés
                    </span>
                    {tagENInputError && (
                      <p className="text-red-500">{tagENInputError}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {tagsEN.map((tag) => (
                        <div
                          key={tag.tag}
                          className="flex items-center justify-between gap-2 bg-gray-500 p-2 rounded-md"
                        >
                          <span className="text-sm text-white">{tag.tag}</span>
                          <button
                            className="text-sm text-white"
                            onClick={() => handleRemoveTagEN(tag.tag)}
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {isMobile && currentStep === 3 && (
                  <ReviewSummary
                    title={watchedFields.title}
                    description={watchedFields.description}
                    titleEN={watchedFields.titleEN}
                    descriptionEN={watchedFields.descriptionEN}
                    tags={tags}
                    tagsEN={tagsEN}
                    hasImage={hasImage}
                    image={image}
                    imageEN={imageEN}
                    {...(showAudienceSelector
                      ? {
                          audienceLabel:
                            selectedAudiences.length === 0
                              ? 'General (todos los usuarios)'
                              : `Restringida a ${selectedAudiences.length} audiencia(s)`,
                        }
                      : {})}
                  />
                )}

                {showAudienceSelector && audiencesEnabled && isStepVisible(3) && (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">Audiencia</label>
                    <AudienceSelect
                      value={selectedAudiences}
                      onChange={setSelectedAudiences}
                      emptyLabel="General: la publicación será visible para todos los usuarios."
                      helpText="Si seleccionas una o más audiencias, solo esos usuarios verán la publicación y recibirán la notificación. Podrás liberarla a todos más adelante editándola."
                    />
                  </div>
                )}

                <div className="flex justify-center gap-3 mt-3">
                  {isMobile && currentStep > 0 && (
                    <Button
                      key="wizard-back-button"
                      label="Atrás"
                      type="button"
                      variant="secondary"
                      onClick={goToPreviousStep}
                      className="w-[120px]"
                    />
                  )}
                  {isMobile && currentStep < totalSteps - 1 ? (
                    <Button
                      key="wizard-next-button"
                      label="Siguiente"
                      type="button"
                      onClick={goToNextStep}
                      className="w-[160px]"
                    />
                  ) : (
                    <Button
                      key="wizard-save-button"
                      label="Guardar"
                      type="submit"
                      isLoading={isLoading}
                      onClick={() => methods.handleSubmit(onSubmit)}
                      disabled={isGuardarCoolingDown}
                      icon={<FiSave />}
                      className="w-[200px]"
                    />
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default FormAddDocument;
